import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import {
  fetchClubPurchaseOrderStatus,
  fetchClubPurchaseOrderStatusByInvoiceId,
} from '@/features/payments/payments.api';
import {
  clearPurchaseAttempt,
  readPurchaseAttempt,
  submitHostedPaymentSession,
} from '@/features/payments/payment-session';
import { buildClubHref } from '@/features/catalog/catalog.api';
import { useTelegramBackButton } from '@/telegram';
import { trackEvent } from '@/shared/lib/analytics';
import { trackYandexGoal } from '@/shared/lib/yandex-metrica';
import { PageFrame } from '@/shared/components/ui/PageFrame';
import { StackHeader } from '@/shared/components/ui/StackHeader';
import { MiniappSurface } from '@/shared/components/ui/MiniappSurface';
import { PrimaryButton } from '@/shared/components/ui/PrimaryButton';
import { NeutralLoadingState } from '@/shared/components/ui/NeutralLoadingState';
import { ErrorState } from '@/shared/components/ui/ErrorState';
import { NeutralStatePanel } from '@/shared/components/ui/NeutralStatePanel';

const SUPPORT_USERNAME = import.meta.env['VITE_SUPPORT_USERNAME'] ?? 'inhoundsupport';
const SUPPORT_EMAIL = import.meta.env['VITE_SUPPORT_EMAIL'] ?? 'info@inhound.club';

type OutcomeHint = 'success' | 'fail';

function openExternal(url: string) {
  if (typeof window === 'undefined') return;
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    window.location.assign(url);
  }
}

function getSupportUrl() {
  return `https://t.me/${SUPPORT_USERNAME}`;
}

function getSupportEmailUrl(subject: string) {
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;
}

function normalizeSource(value: string | null | undefined) {
  return value === 'my-clubs' ? 'my-clubs' : 'catalog';
}

function getSearchValue(searchParams: URLSearchParams, keys: string[]) {
  for (const key of keys) {
    const value = searchParams.get(key);
    if (value) return value;
  }
  return null;
}

function RobokassaReturnPage({ outcomeHint }: { outcomeHint: OutcomeHint }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const rememberedAttempt = useMemo(() => readPurchaseAttempt(), []);
  const invoiceId = getSearchValue(searchParams, ['InvId', 'invId', 'invoiceId']);
  const orderIdFromQuery = getSearchValue(searchParams, ['Shp_orderId', 'shp_orderId', 'orderId']);
  const orderId = orderIdFromQuery ?? (invoiceId ? null : rememberedAttempt?.orderId ?? null);
  const source = normalizeSource(rememberedAttempt?.source);
  const statusQuery = useQuery({
    queryKey: ['payment-return-status', orderId, invoiceId],
    enabled: Boolean(orderId || invoiceId),
    queryFn: () => {
      if (orderId) {
        return fetchClubPurchaseOrderStatus(orderId);
      }
      return fetchClubPurchaseOrderStatusByInvoiceId(invoiceId!);
    },
    refetchInterval: (query) => {
      const response = query.state.data;
      if (!response) return 3000;
      if (response.entitlementActive) return false;
      if (response.order.status === 'failed' || response.order.status === 'expired') return false;
      return 3000;
    },
    refetchIntervalInBackground: true,
    retry: 1,
  });

  const clubId = statusQuery.data?.order.clubId ?? rememberedAttempt?.clubId ?? null;
  const paywallSlug = statusQuery.data?.order.paywallSlug ?? rememberedAttempt?.paywallSlug ?? null;
  const paywallReference = paywallSlug ?? clubId;
  const clubHref = clubId ? buildClubHref({ clubId, paywallSlug }, source) : '/catalog';
  const paywallHref = paywallReference ? `/paywall/${paywallReference}?source=${source}` : '/catalog';
  const clubTitle = statusQuery.data?.order.clubTitle ?? 'Клуб';
  const supportUrl = getSupportUrl();
  const supportEmailUrl = getSupportEmailUrl('Проблема с оплатой клуба INHOUND');
  const orderStatus = statusQuery.data?.order.status ?? null;
  const entitlementActive = statusQuery.data?.entitlementActive ?? false;
  const isPending = statusQuery.data
    ? !entitlementActive && (orderStatus === 'pending' || orderStatus === 'paid')
    : false;
  const isFailed = statusQuery.data
    ? !entitlementActive && (orderStatus === 'failed' || orderStatus === 'expired')
    : false;
  const stateKey = entitlementActive ? 'success' : isPending ? 'pending' : isFailed ? 'fail' : 'unknown';
  const trackedStateRef = useRef<string | null>(null);
  const successDedupeKey = orderId ?? invoiceId ?? `${clubId ?? 'unknown-club'}:${rememberedAttempt?.createdAt ?? 'unknown-attempt'}`;

  const onBack = () => {
    if (entitlementActive) {
      void navigate(clubHref, { replace: true });
      return;
    }

    if (isFailed) {
      void navigate(paywallHref, { replace: true });
      return;
    }

    void navigate(clubHref, { replace: true });
  };

  useTelegramBackButton(onBack, true);

  useEffect(() => {
    if (trackedStateRef.current === stateKey) return;

    if (stateKey === 'pending') {
      trackEvent('payment_pending_view', {
        outcomeHint,
        clubId,
        orderId,
        invoiceId,
      });
    } else if (stateKey === 'success') {
      trackEvent('payment_success_view', {
        clubId,
        orderId,
        invoiceId,
      });
      trackYandexGoal(
        'purchase_success_view',
        {
          club_id: clubId ?? undefined,
          club_slug: paywallSlug ?? undefined,
          source_surface: 'payment_return',
          demo_context: rememberedAttempt?.demoContext ?? undefined,
          order_price: statusQuery.data?.order.amount,
          currency: statusQuery.data?.order.currency,
        },
        {
          dedupeKey: successDedupeKey,
        },
      );
      if (rememberedAttempt?.demoContext) {
        trackYandexGoal(
          'demo_purchase_success_view',
          {
            club_id: clubId ?? undefined,
            club_slug: paywallSlug ?? undefined,
            chapter_id: rememberedAttempt.chapterId ?? undefined,
            source_surface: 'payment_return',
            demo_context: rememberedAttempt.demoContext,
          },
          {
            dedupeKey: successDedupeKey,
          },
        );
      }
    } else if (stateKey === 'fail') {
      trackEvent('payment_fail_view', {
        outcomeHint,
        clubId,
        orderId,
        invoiceId,
        orderStatus,
      });
    }

    trackedStateRef.current = stateKey;
  }, [
    clubId,
    invoiceId,
    orderId,
    orderStatus,
    outcomeHint,
    paywallSlug,
    rememberedAttempt?.chapterId,
    rememberedAttempt?.demoContext,
    stateKey,
    statusQuery.data?.order.amount,
    statusQuery.data?.order.currency,
    successDedupeKey,
  ]);

  useEffect(() => {
    if (!entitlementActive) return;

    clearPurchaseAttempt();
    void queryClient.invalidateQueries({ queryKey: ['my-clubs'] });
    void queryClient.invalidateQueries({ queryKey: ['profile-access'] });
    if (clubId) {
      void queryClient.invalidateQueries({ queryKey: ['club-page', clubId] });
    }
    if (paywallReference) {
      void queryClient.invalidateQueries({ queryKey: ['paywall-offer', paywallReference] });
    }
  }, [clubId, entitlementActive, paywallReference, queryClient]);

  useEffect(() => {
    if (!isFailed) return;
    clearPurchaseAttempt();
  }, [isFailed]);

  if (!orderId && !invoiceId) {
    return (
      <PageFrame topInset="stack" bottomInset="none" className="space-y-4 pb-6">
        <StackHeader onBack={onBack} label="Оплата" />
        <MiniappSurface className="space-y-4 px-5 py-6">
          <div className="space-y-1">
            <h1 className="text-[24px] font-black uppercase leading-[0.92] tracking-[0.02em] text-[var(--foreground)]">
              Не удалось определить заказ
            </h1>
            <p className="text-[14px] leading-6 text-[rgba(26,26,26,0.64)]">
              В этом окне нет данных о платеже. Вернитесь к клубу и запустите оплату снова.
            </p>
          </div>
          <div className="space-y-2">
            <PrimaryButton onClick={() => void navigate(paywallHref)} className="w-full">
              Вернуться к покупке
            </PrimaryButton>
            <PrimaryButton tone="ghost" onClick={() => openExternal(supportUrl)} className="w-full">
              Написать в поддержку
            </PrimaryButton>
          </div>
        </MiniappSurface>
      </PageFrame>
    );
  }

  if (statusQuery.isLoading) {
    return (
      <PageFrame topInset="stack" bottomInset="none" className="space-y-4 pb-6">
        <StackHeader onBack={onBack} label="Оплата" />
        <NeutralLoadingState rows={3} ariaLabel="Проверяем статус оплаты" />
      </PageFrame>
    );
  }

  if (statusQuery.isError || !statusQuery.data) {
    return (
      <PageFrame topInset="stack" bottomInset="none" className="space-y-4 pb-6">
        <StackHeader onBack={onBack} label="Оплата" />
        <ErrorState
          title="Не удалось проверить оплату"
          description="Попробуйте обновить статус ещё раз. Если проблема повторяется, напишите в поддержку."
          retryLabel="Проверить снова"
          onRetry={() => void statusQuery.refetch()}
        />
      </PageFrame>
    );
  }

  if (entitlementActive) {
    return (
      <PageFrame topInset="stack" bottomInset="none" className="space-y-4 pb-6">
        <StackHeader onBack={onBack} label="Оплата" />
        <MiniappSurface className="space-y-4 px-5 py-6">
          <div className="space-y-1">
            <h1 className="text-[24px] font-black uppercase leading-[0.92] tracking-[0.02em] text-[var(--foreground)]">
              Оплата подтверждена
            </h1>
            <p className="text-[14px] leading-6 text-[rgba(26,26,26,0.64)]">
              Клуб «{clubTitle}» успешно оплачен и уже доступен в вашем аккаунте.
            </p>
          </div>
          <div className="rounded-[18px] bg-[rgba(107,78,230,0.08)] px-4 py-3 text-[13px] leading-5 text-[rgba(56,32,149,0.92)]">
            Доступ к клубу уже выдан. Можно сразу переходить к чтению.
          </div>
          <div className="space-y-2">
            <PrimaryButton
              onClick={() => {
                trackEvent('payment_open_club_click', { clubId, source });
                void navigate(clubHref, { replace: true });
              }}
              className="w-full"
            >
              Открыть клуб
            </PrimaryButton>
            <PrimaryButton
              tone="ghost"
              onClick={() => void navigate(source === 'my-clubs' ? '/my-clubs' : '/catalog', { replace: true })}
              className="w-full"
            >
              {source === 'my-clubs' ? 'В мои клубы' : 'В каталог'}
            </PrimaryButton>
          </div>
        </MiniappSurface>
      </PageFrame>
    );
  }

  if (isPending) {
    return (
      <PageFrame topInset="stack" bottomInset="none" className="space-y-4 pb-6">
        <StackHeader onBack={onBack} label="Оплата" />
        <MiniappSurface className="space-y-4 px-5 py-6">
          <div className="space-y-1">
            <h1 className="text-[24px] font-black uppercase leading-[0.92] tracking-[0.02em] text-[var(--foreground)]">
              {orderStatus === 'paid' ? 'Подтверждаем доступ' : 'Платёж обрабатывается'}
            </h1>
            <p className="text-[14px] leading-6 text-[rgba(26,26,26,0.64)]">
              {orderStatus === 'paid'
                ? 'Оплата уже подтверждена провайдером. Ждём финальную синхронизацию доступа в аккаунте.'
                : 'Мы ждём подтверждение оплаты от Robokassa. Если деньги уже списались, доступ появится автоматически.'}
            </p>
          </div>
          <NeutralStatePanel
            title="Проверяем статус заказа"
            description={`Заказ ${statusQuery.data.order.id}. Не закрывайте окно, пока не обновится статус.`}
            actionLabel={statusQuery.isFetching ? 'Обновляем…' : 'Проверить оплату'}
            onAction={() => {
              trackEvent('payment_pending_poll', { clubId, orderId, invoiceId });
              void statusQuery.refetch();
            }}
          />
          <div className="space-y-2">
            {statusQuery.data.paymentSession ? (
              <PrimaryButton
                onClick={() => {
                  trackEvent('payment_retry_click', { clubId, orderId, invoiceId, source: 'pending' });
                  submitHostedPaymentSession(statusQuery.data!.paymentSession!);
                }}
                className="w-full"
              >
                Вернуться к оплате
              </PrimaryButton>
            ) : null}
            <PrimaryButton tone="ghost" onClick={() => openExternal(supportUrl)} className="w-full">
              Написать в поддержку
            </PrimaryButton>
          </div>
        </MiniappSurface>
      </PageFrame>
    );
  }

  return (
    <PageFrame topInset="stack" bottomInset="none" className="space-y-4 pb-6">
      <StackHeader onBack={onBack} label="Оплата" />
      <MiniappSurface className="space-y-4 px-5 py-6">
        <div className="space-y-1">
          <h1 className="text-[24px] font-black uppercase leading-[0.92] tracking-[0.02em] text-[var(--foreground)]">
            {orderStatus === 'expired' ? 'Срок оплаты истёк' : outcomeHint === 'fail' ? 'Оплата не завершена' : 'Платёж не подтверждён'}
          </h1>
          <p className="text-[14px] leading-6 text-[rgba(26,26,26,0.64)]">
            Попробуйте начать оплату снова. Доступ к клубу откроется только после подтверждённого платежа.
          </p>
        </div>
        <div className="rounded-[18px] bg-[rgba(232,93,42,0.1)] px-4 py-3 text-[13px] leading-5 text-[rgba(123,49,21,0.92)]">
          Если деньги списались, а доступ не появился, напишите в поддержку: @{SUPPORT_USERNAME} или {SUPPORT_EMAIL}.
        </div>
        <div className="space-y-2">
          <PrimaryButton
            onClick={() => {
              trackEvent('payment_retry_click', { clubId, orderId, invoiceId, source: 'failed' });
              void navigate(paywallHref, { replace: true });
            }}
            className="w-full"
          >
            Попробовать ещё раз
          </PrimaryButton>
          <PrimaryButton tone="ghost" onClick={() => openExternal(supportUrl)} className="w-full">
            Написать в Telegram
          </PrimaryButton>
          <PrimaryButton tone="ghost" onClick={() => openExternal(supportEmailUrl)} className="w-full">
            Написать на почту
          </PrimaryButton>
        </div>
      </MiniappSurface>
    </PageFrame>
  );
}

export function RobokassaSuccessPage() {
  return <RobokassaReturnPage outcomeHint="success" />;
}

export function RobokassaFailPage() {
  return <RobokassaReturnPage outcomeHint="fail" />;
}
